<template>
    <div>
        <BaseSidebar v-if="$route.matched.length == 2 && showSidebar" v-model="showSidebar">
            <SearchModule
                v-model="selected"
                class="flex-grow"
                filterOrientation="vertical"
                hideSearch
                :filters="getFilters()"
                :hasFiltersChanged="hasFiltersChanged"
                @clear:filter="
                    () => {
                        clearFilters();
                        resetPageScroll();
                    }
                "
                @filter:change="resetPageScroll"
            />
        </BaseSidebar>

        <SubLayout v-if="$route.matched.length == 2" columns="1fr auto">
            <SearchModule
                slot="top"
                v-model="selected"
                filterOrientation="horizontal"
                :filters="getFilters()"
                :hasFiltersChanged="hasFiltersChanged"
                @clear:filter="
                    () => {
                        clearFilters();
                        resetPageScroll();
                    }
                "
                @filter:change="resetPageScroll"
            />

            <div slot="mobtop">
                <SearchModule
                    v-model="selected"
                    filterOrientation="horizontal"
                    :hasFiltersChanged="hasFiltersChanged"
                    @clear:filter="
                        () => {
                            clearFilters();
                            resetPageScroll();
                        }
                    "
                    @filter:change="resetPageScroll"
                >
                    <div
                        class="text-center py-2 subheading secondary-color noselect"
                        @click="() => (showSidebar = !showSidebar)"
                    >
                        {{ $t('findjobs.changeFilters') }}
                    </div>
                </SearchModule>
            </div>

            <div slot="center" class="d-flex flex-row w-100">
                <Pagination ref="pagination" class="w-100" @onBottom="nextPage">
                    <PostingModule
                        :data="data"
                        class="mb-3 w-100"
                        :resolveRoute="resolvePostingRoute"
                        :isLoading="loading()"
                    />
                </Pagination>
            </div>

            <div slot="right">
                <WebinarList
                    v-if="$route.matched.length == 2"
                    :value="events"
                    :vFairId="$route.params.id"
                    class="mb-4"
                    width="288px"
                />
            </div>
        </SubLayout>
        <router-view></router-view>
    </div>
</template>

<script>
import PostingModule from '~/shared/postingmodule/PostingModule';
import WebinarList from './components/WebinarList';
import Pagination from '../../../components/miscellaneous/Pagination';
import SubLayout from '../../../components/layouts/mainlayout/SubLayout';
import BaseSidebar from '../../../components/builders/Base/Sidebar';
import SearchModule from '../../../shared/searchmodule/SearchModule';
import AuthService from '../../../services/AuthService';
import jobFilters, { i18nMap, filterMap } from '~/data/jobFilters';
import emptySelected from '~/utils/emptySelected';
import mapSelectedToFilters from '~/utils/mappers/mapSelectedToFilters';
import CompanyService from '~/services/CompanyService';
import { uuidFromURLParam } from '~/utils/urlUtils';

export default {
    name: 'Jobs',
    components: {
        SearchModule,
        BaseSidebar,
        SubLayout,
        Pagination,
        PostingModule,
        WebinarList,
    },
    data() {
        return {
            companies: [],
            companiesLoading: true,
            jobRequest: undefined,
            selected: null,
            forceFilterUpdate: false,
            showSidebar: false,
            waitForFilterProcessing: true,
        };
    },
    computed: {
        vFairId() {
            return uuidFromURLParam(this.$route.params.id);
        },
        postings() {
            return this.$interfaces.posting.get(
                {
                    reactive: true,
                    ...mapSelectedToFilters(this.selectedFilters),
                    vfair_id: this.vFairId,
                    page_size: 25,
                    page: this.page,
                },
                1,
                this.page
            );
        },
        data() {
            if (!this.$route.query.pavilion_id) return this.postings.data;

            if (!this.postings.data || this.companiesLoading || !this.companies.length) return [];
            const companyIds = this.companies.map(company => company.organizationId);

            return this.postings.data.filter(posting =>
                companyIds.includes(parseInt(posting.companyId, 10))
            );
        },
        page() {
            return this.$interfaces.scrollState.findJobsPage();
        },
        events() {
            if (this.vFairId) {
                const eventsRequest = this.$interfaces.vfair.id(this.vFairId).events.get({
                    reactive: true,
                });
                if (!eventsRequest.loading) {
                    const events = eventsRequest.data;
                    if (!this.$route.query.pavilion_id) {
                        return events;
                    }
                    if (this.companiesLoading || !this.companies.length) {
                        return [];
                    }

                    const companyIds = this.companies.map(company => company.organizationId);
                    return events.filter(event => companyIds.includes(event.ownerOrganizationId));
                }
            }
            return [];
        },
        filters() {
            return this.isLoggedIn && this.connections.data
                ? [
                      ...jobFilters(),
                      this.filteredConnections.length > 0
                          ? {
                                ...i18nMap('filterNames')('exclude_school_id'),
                                options: [
                                    ...this.connections.data
                                        .filter(it => it.isVerified)
                                        .map(it =>
                                            filterMap(
                                                it.school.subDomain
                                                    ? it.school.subDomain.toUpperCase()
                                                    : this.$t(
                                                          'cts.excludeSchoolIds.unknownSchoolId'
                                                      ),
                                                it.schoolId
                                            )
                                        ),
                                    i18nMap('excludeSchoolIds')('-1'),
                                ],
                            }
                          : null, // Can be null, needs to be filtered
                  ].filter(e => e) // Filters the null values
                : jobFilters();
        },
        isLoggedIn() {
            return AuthService.isLoggedIn();
        },
        connections() {
            return this.$interfaces.university.connection.get({
                reactive: true,
            });
        },
        filteredConnections() {
            return this.connections.data.filter(it => it.isVerified);
        },
        hasFiltersChanged() {
            return false;
            // if (this.forceFilterUpdate) {
            //     this.forceFilterUpdate = false;
            //     this.clearFilters();
            // }

            // if (!this.selectedFilters) return false;

            // let changed = false;

            // let empty = Object.entries(emptySelected(this.filters));

            // empty.forEach(([key]) => {
            //     let temp = this.selectedFilters[key];
            //     if (temp && Object.entries(temp).length > 0) {
            //         changed = true;
            //     }
            // });

            // return changed || Boolean(this.selectedFilters['company_names']);
        },
        selectedFilters() {
            if (
                Object.values(this.selected).reduce(
                    (prev, curr) => prev + Object.values(curr).length,
                    0
                ) === 0
            )
                return {};
            else return this.selected;
        },
    },
    methods: {
        async fetchCompaniesData() {
            if (!this.vFairId) return;

            const pavilionId = this.$route.query.pavilion_id;
            const result = await CompanyService.getVFairCompanies(
                this.vFairId,
                typeof pavilionId === 'string' ? pavilionId : undefined
            );

            this.companiesLoading = false;
            this.companies = result.data.filter(c => c.status === 2);
        },
        resolvePostingRoute(jobAdId) {
            let url = this.$URLS.vfair.concat('/', this.$route.params.id).concat('/jobs/', jobAdId);
            this.$route.query.pavilion_id &&
                (url += `?pavilion_id=${this.$route.query.pavilion_id}`);

            return url;
        },
        loading() {
            return this.postings.loading;
        },
        nextPage() {
            if (this.loading() || this.data.length < 4) {
                return;
            }
            this.$interfaces.scrollState.setFindJobsPage(this.page + 1);
        },
        getFilters() {
            return this.filters;
        },
        clearFilters() {
            this.selected = emptySelected(this.filters);
        },
        resetPageScroll() {
            // Filters changed, need to reset page number and pagination anchors
            this.$interfaces.scrollState.setFindJobsPage(1);
            this.$refs.pagination.resetCurrentAnchor();
        },
        addQueryToFilters() {
            // Adds filters in query to selected
            if (!this.$route.query) {
                this.waitForFilterProcessing = false;
                return;
            }

            const selected = {};
            const query = Object.assign({}, this.$route.query);

            // Adding all filters in query to selected
            for (let filterName in query) {
                if (filterName === 'pavilion_id') {
                    selected[filterName] = query[filterName];
                    continue;
                }
                if (filterName === 'openLogin') continue;

                if (!selected[filterName]) {
                    selected[filterName] = {};
                }
                const filter = query[filterName];
                if (filter instanceof Array) {
                    filter.forEach(filterValue => {
                        selected[filterName][filterValue] = 1;
                    });
                } else if (filterName === 'query') {
                    selected[filterName] = query[filterName];
                } else {
                    selected[filterName][query[filterName]] = 1;
                }
            }

            // Set selected filters
            this.$nextTick(() => {
                this.selected = {
                    ...this.selected,
                    ...selected,
                };
                this.waitForFilterProcessing = false;
            });
        },
        addFiltersToQuery(filters) {
            const query = {}; // Empty query

            const pavilionId = this.$route.query.pavilion_id;
            if (!!pavilionId) {
                query['pavilion_id'] = pavilionId;
            }

            // Add all selected filters to query
            Object.entries(filters).forEach(([filterName, filterObject]) => {
                let filterValues = null;
                // Incase filter is a string (like search query)
                if (typeof filterObject === 'string') {
                    filterValues = filterObject;
                } else {
                    // filterObject is an object, get keys
                    filterValues = Object.keys(filterObject);
                }

                if (filterValues.length > 0) {
                    query[filterName] = filterValues;
                }
            });

            // Update url query
            this.$router.replace({ query });
        },
    },
    watch: {
        filters() {
            if (!this.selected) {
                this.selected = emptySelected(null);
            }
        },
        selected(filters) {
            if (filters) {
                this.addFiltersToQuery(filters);
            }
        },
        queries(newQueries, oldQueries) {
            if (oldQueries && Object.keys(oldQueries).length !== 0) {
                if (Object.keys(newQueries).length === 0) {
                    // Force refresh of filters
                    this.forceFilterUpdate = true;
                }
            }
        },
    },
    created() {
        this.$interfaces.scrollState.setFindJobsPage(1);
        this.selected = emptySelected(this.filters);
    },
    mounted() {
        this.fetchCompaniesData();
        this.addQueryToFilters();
    },
};
</script>

<style scoped></style>
